import {DateTime} from 'luxon';
import React, {FC, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {extractInitials} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import avatarPlaceholderImage from '../../../../assets/images/avatarPlaceholder.svg';
import {Avatar} from '../../../../components/common/avatars/avatar';
import {AvatarBackground} from '../../../../components/common/avatars/avatarBackground';
import {AvatarImage} from '../../../../components/common/avatars/avatarImage';
import {AvatarInitials} from '../../../../components/common/avatars/avatarInitials';
import {Skeleton} from '../../../../components/common/skeleton';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsMetricError,
  AnalyticsTableMetricsEnum,
  isContactDataPoint,
  isNumericDataPoint,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {makeGetAnalyticsMetric} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {
  ContributingSegmentsOptionsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {ReportGridItem} from '../../reportViewer/reportGrid';
import {MetricErrorState} from '../charts/common/metricErrorState';
import {defaultEmptyStateRenderer, EmptyStateRenderer, isEmptyMetric} from '../charts/emptyState';
import {ReportElementProps, ReportElementWrapperProps} from '../reportElement';
import {mapMetricRowToTableRow} from '../table/tableHelpers';

/*
 * Types.
 */

interface SurveySampleFeedbackRow {
  contactId: number;
  // This can be either a full name or an email handle.
  contactDisplayName: string;
  contactAvatarUrl: string;
  csatSurveyScore: number;
  csatComment: string | undefined;
  csatAnnotationId: number;
  timestamp: number;
}

/*
 * Constants.
 */

// Clicking anywhere in the row should use this column for metric details.
const MetricDetailsColumn = 'csat_survey_score';

const maxSampleFeedbackRowCount = 3;
const defaultWidth = 240;
const defaultHeight = 220;
const avatarSize = 40;

const maxCommentLength = 62;

const anonymousName = (
  <Trans id="analytics-customer-experience-report-anonymous-contact-name">Anonymous</Trans>
);

/*
 * Style.
 */

const StyledFeedbackButtonStyle = css`
  position: relative;
  padding: 8px 12px;
  /* Want the feedback button aligned with the title despite its padding. */
  left: -12px;
  border-radius: 12px;
`;

const StyledFeedbackButton = styled(ChromelessButton)`
  ${StyledFeedbackButtonStyle}

  &:hover {
    background-color: ${(p) => p.theme.greys.shade10};
  }
`;

const StyledFeedbackHeaderDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledFeedbackSkeletonDiv = styled.div`
  ${StyledFeedbackButtonStyle}
`;

const StyledContactAvatar = styled(Avatar)`
  margin-right: 16px;
`;

const StyledContactHandleDiv = styled.div`
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};

  margin-bottom: 4px;

  max-width: 200px;
  ${ellipsis()};
`;

const StyledStarsDateWrapperDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledStarIcon = styled(Icon)`
  display: inline-block;
`;

const StyledDateSpan = styled.span`
  margin-left: 8px;

  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledCsatCommentDiv = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledAnonymousAvatar = styled(AvatarImage)`
  background-size: 40px;
  background-position: center 5px;
`;

/*
 * Props.
 */

interface SurveySampleFeedbackTableProps
  extends ReportElementProps<AnalyticsTableMetricsEnum>,
    ReportElementWrapperProps {}

/*
 * Component.
 */

export const SurveySampleFeedbackTable: FC<SurveySampleFeedbackTableProps> = ({
  isLoading,
  metric,
  renderEmptyState = defaultEmptyStateRenderer,
}) => {
  const dispatch = useAppDispatch();

  const [metricData, metricError] = useAppSelector(makeGetAnalyticsMetric(metric));

  const isEmpty = !isLoading && isEmptyMetric(metricData);

  const surveySampleFeedbackRows = useMemo(() => {
    if (!metricData || metricData.rows?.length === 0) {
      return [];
    }

    return metricData.rows
      .slice(0, maxSampleFeedbackRowCount)
      .map((row) => {
        const metricRow = mapMetricRowToTableRow(metricData.columns, row);

        if (!isContactDataPoint(metricRow.contact_id) || !isNumericDataPoint(metricRow.timestamp)) {
          return undefined;
        }

        const contactId = metricRow.contact_id.id;
        const contactDisplayName = metricRow.contact_id.value;
        const contactAvatarUrl = metricRow.contact_id.avatarUrl;
        const timestamp = metricRow.timestamp.value;
        // csat_survey_score is a keyword (string).
        const csatSurveyScore = Number(metricRow.csat_survey_score?.value ?? 0);
        const csatComment = metricRow.csat_annotation_id?.value;
        const csatAnnotationId = metricRow.csat_resource_id?.value;

        return {
          contactId,
          contactDisplayName,
          contactAvatarUrl,
          csatSurveyScore,
          csatComment,
          csatAnnotationId,
          timestamp,
        };
      })
      .filter((row): row is SurveySampleFeedbackRow => Boolean(row));
  }, [metricData]);

  const onRowSelect = (rowId: number) => {
    dispatch(
      setMetricDetailsAction(metric, {
        [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
          [ContributingSegmentsOptionsEnum.SUB_METRIC]: MetricDetailsColumn,
          [ContributingSegmentsOptionsEnum.ROW]: rowId,
        },
      }),
    );
  };

  const previewContent = renderPreviewContent(isEmpty, isLoading, renderEmptyState, metricError);
  const content = previewContent ?? renderSampleFeedbackRows(surveySampleFeedbackRows, onRowSelect);

  return <ReportGridItem>{content}</ReportGridItem>;
};

/*
 * Helpers.
 */

function renderPreviewContent(
  isEmpty: boolean,
  isLoading: boolean,
  renderEmptyState: EmptyStateRenderer,
  metricError?: AnalyticsMetricError,
) {
  if (metricError) {
    return <MetricErrorState error={metricError} width={defaultWidth} height={defaultHeight} />;
  }

  if (isEmpty) {
    return renderEmptyState({width: defaultWidth, height: defaultHeight});
  }

  if (isLoading) {
    return renderLoadingState();
  }

  return undefined;
}

function renderSampleFeedbackRows(rows: SurveySampleFeedbackRow[], onRowSelect: HandlerOf<number>) {
  return rows.map((row, index) => renderSampleFeedbackRow(row, index, onRowSelect));
}

function renderSampleFeedbackRow(
  row: SurveySampleFeedbackRow,
  index: number,
  onRowSelect: HandlerOf<number>,
) {
  const onClick = () => {
    onRowSelect(row.csatAnnotationId);
  };

  return (
    <StyledFeedbackButton key={index} onClick={onClick}>
      <StyledFeedbackHeaderDiv>
        {renderContactAvatar(row.contactDisplayName, row.contactAvatarUrl)}
        <div>
          {renderContactDisplayName(row.contactDisplayName)}
          <StyledStarsDateWrapperDiv>
            {renderStars(row.csatSurveyScore)}
            {renderDate(row.timestamp)}
          </StyledStarsDateWrapperDiv>
        </div>
      </StyledFeedbackHeaderDiv>
      {renderComment(row.csatComment)}
    </StyledFeedbackButton>
  );
}

function renderContactAvatar(contactDisplayName: string, contactAvatarUrl: string) {
  const avatarContent = renderAvatar(contactDisplayName, contactAvatarUrl);

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledContactAvatar size={avatarSize}>
          <AvatarBackground color={theme.greys.shade70} />
          {avatarContent}
        </StyledContactAvatar>
      )}
    </UserThemeConsumer>
  );
}

function renderAvatar(contactDisplayName: string, contactAvatarUrl: string) {
  if (contactAvatarUrl) {
    return renderAvatarImage(contactAvatarUrl);
  }

  if (contactDisplayName) {
    return renderAvatarInitials(contactDisplayName);
  }

  return renderAnonymousAvatar();
}

function renderAvatarImage(contactAvatarUrl: string) {
  return <AvatarImage imageUrl={contactAvatarUrl} />;
}

function renderAvatarInitials(contactDisplayName: string) {
  const initials = extractInitials(contactDisplayName);

  return (
    <UserThemeConsumer>
      {(theme) => <AvatarInitials size={avatarSize} textColor={theme.greys.white} value={initials} />}
    </UserThemeConsumer>
  );
}

function renderAnonymousAvatar() {
  return <StyledAnonymousAvatar imageUrl={avatarPlaceholderImage} />;
}

function renderContactDisplayName(contactDisplayName: string) {
  return <StyledContactHandleDiv>{contactDisplayName || anonymousName}</StyledContactHandleDiv>;
}

function renderStars(starCount: number) {
  return (
    <UserThemeConsumer>
      {(theme) => {
        const starColor = computeStarColor(starCount, theme);
        const stars = [...Array(starCount).keys()].map((index) => (
          <StyledStarIcon key={index} name="starFilledSmall" color={starColor} />
        ));

        const unfilledStarCount = 5 - starCount;
        const unfilledStars = [...Array(unfilledStarCount).keys()].map((index) => (
          <StyledStarIcon key={starCount + index} name="starSmall" color={theme.palette.grey.shade30} />
        ));

        return (
          <span>
            {stars}
            {unfilledStars}
          </span>
        );
      }}
    </UserThemeConsumer>
  );
}

function computeStarColor(score: number, theme: UserTheme) {
  switch (score) {
    case 5:
      return theme.palette.blue.shade50;
    case 4:
      return theme.palette.blue.shade40;
    case 3:
      return theme.palette.blue.shade30;
    case 2:
      return theme.palette.blue.shade20;
    case 1:
    default:
      return theme.palette.blue.shade10;
  }
}

function renderDate(timestamp: number) {
  const date = DateTime.fromMillis(timestamp);
  const shortMonth = date.toLocaleString({month: 'short'}, {locale: 'en-us'});

  return (
    <StyledDateSpan>
      {shortMonth} {date.day}
    </StyledDateSpan>
  );
}

function renderComment(comment: string | undefined) {
  const trimmedComment = comment?.trim(); // Sometimes users will leave trailing spaces in their comments. It's not pretty.
  if (!trimmedComment) {
    return null;
  }

  // We want the comment wrapped up with double quotes,
  // but as far as I know it is not possible to combine an ellipsis with -webkit-line-clamp while also adding a custom character at the end of the ellipsis.
  // So using a simple slice instead.
  const needsEllipsis = trimmedComment.length > maxCommentLength;

  const shortCommentWithEllipsis = needsEllipsis
    ? `${trimmedComment.slice(0, maxCommentLength)}…`
    : trimmedComment;
  return <StyledCsatCommentDiv>&quot;{shortCommentWithEllipsis}&quot;</StyledCsatCommentDiv>;
}

function renderLoadingState() {
  return Array.from({length: maxSampleFeedbackRowCount}, (_, index) => (
    <StyledFeedbackSkeletonDiv key={index}>
      <Skeleton width="100%" height="40px" />
    </StyledFeedbackSkeletonDiv>
  ));
}
